import { Locale } from "../Locale.ts";

/**
 * The base-ten blocks manipulative's labels (ADR 0117). The tens and ones columns can hold ten or
 * more, so the hints and trade buttons name the exchange in words; `{count}` is filled by format().
 */
const en = {
    "baseTen.hundreds": "Hundreds",
    "baseTen.tens": "Tens",
    "baseTen.ones": "Ones",
    "baseTen.addHundred": "Add a hundred",
    "baseTen.removeHundred": "Take away a hundred",
    "baseTen.addTen": "Add a ten",
    "baseTen.removeTen": "Take away a ten",
    "baseTen.addOne": "Add a one",
    "baseTen.removeOne": "Take away a one",
    "baseTen.drawHundreds": "Flats drawn: {count}",
    "baseTen.drawTens": "Rods drawn: {count}",
    "baseTen.drawOnes": "Unit cubes drawn: {count}",
    "baseTen.tradeOnes": "Trade 10 ones for 1 ten",
    "baseTen.breakTen": "Break 1 ten into 10 ones",
    "baseTen.tradeTens": "Trade 10 tens for 1 hundred",
    "baseTen.breakHundred": "Break 1 hundred into 10 tens",
    "baseTen.hintOnes": "10 or more ones: trade ten of them for a ten.",
    "baseTen.hintTens": "10 or more tens: trade ten of them for a hundred.",
    "baseTen.readout": "Current number",
    "baseTen.buildLabel": "Build a number (0 to 999)",
    "baseTen.buildButton": "Build",
    "baseTen.clear": "Clear",
    "baseTen.buildError": "Enter a whole number from 0 to 999.",
} as const;

export type BaseTenStringKey = keyof typeof en;

const es = {
    "baseTen.hundreds": "Centenas",
    "baseTen.tens": "Decenas",
    "baseTen.ones": "Unidades",
    "baseTen.addHundred": "Añadir una centena",
    "baseTen.removeHundred": "Quitar una centena",
    "baseTen.addTen": "Añadir una decena",
    "baseTen.removeTen": "Quitar una decena",
    "baseTen.addOne": "Añadir una unidad",
    "baseTen.removeOne": "Quitar una unidad",
    "baseTen.drawHundreds": "Placas dibujadas: {count}",
    "baseTen.drawTens": "Barras dibujadas: {count}",
    "baseTen.drawOnes": "Cubos dibujados: {count}",
    "baseTen.tradeOnes": "Cambiar 10 unidades por 1 decena",
    "baseTen.breakTen": "Descomponer 1 decena en 10 unidades",
    "baseTen.tradeTens": "Cambiar 10 decenas por 1 centena",
    "baseTen.breakHundred": "Descomponer 1 centena en 10 decenas",
    "baseTen.hintOnes": "10 o más unidades: cambia diez de ellas por una decena.",
    "baseTen.hintTens": "10 o más decenas: cambia diez de ellas por una centena.",
    "baseTen.readout": "Número actual",
    "baseTen.buildLabel": "Forma un número (de 0 a 999)",
    "baseTen.buildButton": "Formar",
    "baseTen.clear": "Borrar",
    "baseTen.buildError": "Escribe un número entero de 0 a 999.",
} satisfies Record<BaseTenStringKey, string>;

const fr = {
    "baseTen.hundreds": "Centaines",
    "baseTen.tens": "Dizaines",
    "baseTen.ones": "Unités",
    "baseTen.addHundred": "Ajouter une centaine",
    "baseTen.removeHundred": "Retirer une centaine",
    "baseTen.addTen": "Ajouter une dizaine",
    "baseTen.removeTen": "Retirer une dizaine",
    "baseTen.addOne": "Ajouter une unité",
    "baseTen.removeOne": "Retirer une unité",
    "baseTen.drawHundreds": "Plaques dessinées : {count}",
    "baseTen.drawTens": "Barres dessinées : {count}",
    "baseTen.drawOnes": "Cubes dessinés : {count}",
    "baseTen.tradeOnes": "Échanger 10 unités contre 1 dizaine",
    "baseTen.breakTen": "Décomposer 1 dizaine en 10 unités",
    "baseTen.tradeTens": "Échanger 10 dizaines contre 1 centaine",
    "baseTen.breakHundred": "Décomposer 1 centaine en 10 dizaines",
    "baseTen.hintOnes": "10 unités ou plus : échangez-en dix contre une dizaine.",
    "baseTen.hintTens": "10 dizaines ou plus : échangez-en dix contre une centaine.",
    "baseTen.readout": "Nombre actuel",
    "baseTen.buildLabel": "Former un nombre (de 0 à 999)",
    "baseTen.buildButton": "Former",
    "baseTen.clear": "Effacer",
    "baseTen.buildError": "Écrivez un nombre entier de 0 à 999.",
} satisfies Record<BaseTenStringKey, string>;

export const baseTenStrings: Record<Locale, Record<BaseTenStringKey, string>> = { en, es, fr };
